import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { client } from '../../utils/api-client'
import { updateAuthCookie } from '../../utils/tokenManager'
import { useForm, SubmitHandler } from 'react-hook-form'
import useTimer from '../../hooks/useTimer'
import { makeNumbersFarsi, makeNumbersLatin } from '../../utils/farsi'
import { Helmet } from 'react-helmet-async'

interface IFormInputs {
  username: string
  password: string
  verificationCode: number
}

interface FromPropTypes {
  pathname?: string
  orderId?: string
  from?: string
  [key: string]: any
}

export const VerificationCodeForm = ({
  username,
  from,
}: {
  username: string
  from?: FromPropTypes
}) => {
  const [verifyCode, setVerifyCode] = useState('')
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<IFormInputs>()
  const navigate = useNavigate()

  const [timerFinish, setTimerFinish] = useState(false)
  const timer = useTimer({
    from: 122,
    to: 0,
    intervalSecond: 1,
    finished: (value) => setTimerFinish(true),
  })

  useEffect(() => {
    timer.start()
    if (timer.value === 0) timer.pause()
  }, [timer])

  const requestResendVerificationCode = async () => {
    const normalizedCellphone = makeNumbersLatin(String(username))
    const data = {
      cellphone: normalizedCellphone,
      client_id: '',
    }
    timer.restart(122)
    await client('phones-verification-codes', { data })
  }

  const onSubmitVerificationCode: SubmitHandler<IFormInputs> = async (
    options,
  ) => {
    const normalizedUsername = makeNumbersLatin(String(username))
    const normalizedCode = makeNumbersLatin(
      String(options.verificationCode ?? ''),
    )
    try {
      const data = {
        grant_type: 'verification_code',
        verificationCode: normalizedCode,
        username: normalizedUsername,
        cellphone: normalizedUsername,
      }

      const result = await client('oauth/token', { data })

      // If we get here, user is registered - complete the login flow
      const authToken = {
        accessToken: result.access_token,
        refreshToken: result.refresh_token,
        expiresAt: Date.now() + result.expires_in * 1000,
      }

      // Use centralized token management
      updateAuthCookie(authToken)

      // Fetch user data and check if profile is complete
      const userToken = authToken.accessToken
      const userData = await client('customer', { userToken })

      // Check if profile is incomplete
      if (userData.profileComplete === false) {
        return navigate('/sign-up', {
          state: {
            verifyCode: normalizedCode,
            username: normalizedUsername,
            from,
          },
        })
      }

      // Profile is complete, navigate based on where user came from
      if (from?.pathname) {
        navigate('/cart/shipping-info', {
          state: { orderId: from?.orderId },
        })
      } else if (from?.from === 'product-request') {
        // Redirect back to main page with form state
        navigate('/', {
          state: from,
        })
      } else {
        navigate('/')
      }
    } catch (err: any) {
      // Handle errors
      console.error('Verification code error:', err)
      // You might want to show an error message to the user here
    }
  }

  const handleVerifyCodeChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ): void => {
    const transformedValue = makeNumbersFarsi(event.target.value)
    setVerifyCode(transformedValue)
  }

  const registerWithTransform = {
    ...register('verificationCode', { required: true }),
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
      handleVerifyCodeChange(e)
      register('verificationCode', { required: true }).onChange(e)
    },
  }

  return (
    <div className='my-12'>
      <Helmet>
        <title className='text-xl font-semibold'>کد تایید</title>
        <meta
          name='description'
          content='بوک استوری - هر کتاب یک داستان، هر داستان یک زندگی دوباره'
        />
      </Helmet>
      <form
        className='max-w-lg mt-3 sm:mt-5 mx-auto px-4 sm:px-6'
        onSubmit={handleSubmit(onSubmitVerificationCode)}
      >
        <h1 className='text-base sm:text-lg md:text-xl text-right pb-2 sm:pb-2.5 mb-2 sm:mb-3 leading-relaxed'>
          ما یک کد تایید به شماره تلفن{' '}
          <span className='italic mx-1 font-bold text-base sm:text-lg md:text-xl'>
            {username}
          </span>
          ارسال کردیم. لطفا کد تایید خود را وارد نمایید:
        </h1>
        {timer.value === 0 ? (
          <p className='mb-2 sm:mb-3'>
            <button
              className='text-xs sm:text-sm text-center w-full sm:w-auto hover:underline transition-all'
              onClick={() => requestResendVerificationCode()}
            >
              اگر هنوز کد فعال‌سازی را دریافت نکرده‌اید، برای ارسال دوباره کلیک
              کنید.
            </button>
          </p>
        ) : (
          <p className='mb-2 sm:mb-3 text-xs sm:text-sm leading-relaxed'>
            اگر تا
            <span className='verification-code-resend-count-down-value mx-1 font-semibold'>
              {makeNumbersFarsi(timer.value)}
            </span>
            ثانیه دیگر پیامک ما را دریافت نکردید، می‌توانید ارسال دوباره آن را
            درخواست دهید.
          </p>
        )}
        <input
          autoFocus
          className='input-theme block box-border w-full rounded border border-solid py-2.5 sm:py-3 px-3 sm:px-4 mb-2 sm:mb-2.5 text-base sm:text-lg text-left'
          value={verifyCode}
          dir='ltr'
          {...registerWithTransform}
        />
        {errors.verificationCode && (
          <p className='text-[#bf1650] text-xs sm:text-sm before:inline before:content-["⚠"] mb-2 sm:mb-2.5'>
            {' '}
            کد تایید الزامی است.
          </p>
        )}
        <input
          className='w-full rounded text-white border-none mt-6 sm:mt-8 md:mt-10 p-3 sm:p-4 text-base sm:text-lg md:text-xl font-bold tracking-wider active:transition-all active:duration-[0.3s] active:opacity-80 cursor-pointer'
          style={{ backgroundColor: 'var(--accent-primary)' }}
          type='submit'
          value=' ادامه'
        />
      </form>
    </div>
  )
}
